// Global Instructions Rule Applied!
import React from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Avatar, Card, List, Typography } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronRight, faEnvelope, faPhone, faGlobe } from '@fortawesome/free-solid-svg-icons'
import { fetchContactById } from '../../../../app_modules/contacts/services/contactsService.ts'
import { selectContactById } from '../../../../state_manager/state_update_actions/contacts/selectors.ts'
import { useRecordDetail } from '../lib/useRecordDetail.ts'
import { useRecordRecent } from '../lib/useRecordRecent.ts'
import { detailPath, avatarInitials } from '../lib/routes.ts'
import { text, personName, externalHref } from '../lib/format.ts'
import { LoadingState, ErrorState, Section } from '../components/ui.tsx'

const telHref = (phone: unknown) => `tel:${String(phone).replace(/\s+/g, '')}`
const mailHref = (email: unknown) => `mailto:${String(email).trim()}`

/**
 * Company-role rows shared by the Roles / Prior roles / Founder of / Introductions
 * sections — the embedded `prog__current_roles` / `prog__prior_roles` /
 * `prog__founder_of` / `prog__introductions` arrays all carry the same
 * `contact_accounts__*` + `prog__account__name` shape. Rows cross-link to the company.
 */
function CompanyRolesList({
  rows,
  emptyText,
  navigate
}: {
  rows: any[]
  emptyText: string
  navigate: (path: string) => void
}): React.JSX.Element {
  if (rows.length === 0) return <Typography.Text type="secondary">{emptyText}</Typography.Text>
  return (
    <List
      size="small"
      dataSource={rows}
      rowKey={(r: any) => String(r.contact_accounts__id ?? r.contact_accounts__account_id)}
      renderItem={(r: any) => {
        const companyId = r.contact_accounts__account_id
        const clickable = Boolean(companyId)
        return (
          <List.Item
            onClick={clickable ? () => navigate(detailPath('companies', companyId)) : undefined}
            style={{ cursor: clickable ? 'pointer' : 'default', paddingLeft: 4, paddingRight: 4 }}
          >
            <List.Item.Meta title={text(r.prog__account__name)} description={r.contact_accounts__title || undefined} />
            {clickable ? <FontAwesomeIcon icon={faChevronRight} style={{ color: 'rgba(0,0,0,0.25)' }} /> : null}
          </List.Item>
        )
      }}
    />
  )
}

/**
 * Contact detail (read-only) with tap-to-call / tap-to-email actions. Reuses
 * `fetchContactById` (slice-first via useRecordDetail). Current/prior roles, founded
 * companies, and introductions all come embedded on the record and cross-link to
 * their company.
 */
export function ContactScreen(): React.JSX.Element {
  const { id } = useParams()
  const navigate = useNavigate()
  const { record: contact, loading, error } = useRecordDetail<any>(id, selectContactById, fetchContactById)
  useRecordRecent(
    'contacts',
    id,
    contact
      ? personName(
          contact.contacts__first_name,
          contact.contacts__last_name,
          contact.contacts__preferred_name,
          contact.contacts__email
        )
      : undefined,
    contact?.contact_accounts__title
  )

  if (loading && !contact) return <LoadingState label="Loading contact…" />
  if (!contact) return <ErrorState message={error} />

  const name = personName(
    contact.contacts__first_name,
    contact.contacts__last_name,
    contact.contacts__preferred_name,
    contact.contacts__email
  )

  const methods: { key: string; label: string; value: string; href: string; icon: any }[] = []
  if (contact.contacts__mobile_phone)
    methods.push({ key: 'mobile', label: 'Mobile', value: String(contact.contacts__mobile_phone), href: telHref(contact.contacts__mobile_phone), icon: faPhone })
  if (contact.contacts__business_phone)
    methods.push({ key: 'business', label: 'Business', value: String(contact.contacts__business_phone), href: telHref(contact.contacts__business_phone), icon: faPhone })
  if (contact.contacts__email)
    methods.push({ key: 'email', label: 'Email', value: String(contact.contacts__email), href: mailHref(contact.contacts__email), icon: faEnvelope })
  if (contact.contacts__email_other)
    methods.push({ key: 'email_other', label: 'Other email', value: String(contact.contacts__email_other), href: mailHref(contact.contacts__email_other), icon: faEnvelope })
  if (contact.contacts__email_private)
    methods.push({ key: 'email_private', label: 'Private email', value: String(contact.contacts__email_private), href: mailHref(contact.contacts__email_private), icon: faEnvelope })
  if (contact.contacts__linked_in_url)
    methods.push({ key: 'linkedin', label: 'LinkedIn', value: String(contact.contacts__linked_in_url), href: externalHref(contact.contacts__linked_in_url), icon: faGlobe })

  const roles: any[] = Array.isArray(contact.prog__current_roles) ? contact.prog__current_roles : []
  const priorRoles: any[] = Array.isArray(contact.prog__prior_roles) ? contact.prog__prior_roles : []
  const founderOf: any[] = Array.isArray(contact.prog__founder_of) ? contact.prog__founder_of : []
  const introductions: any[] = Array.isArray(contact.prog__introductions) ? contact.prog__introductions : []

  return (
    <div style={{ padding: 12 }}>
      <Card size="small" style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Avatar size={48} src={contact.contacts__photo_url || undefined} style={{ backgroundColor: '#1890ff', color: '#fff' }}>
            {avatarInitials(name)}
          </Avatar>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 18, lineHeight: 1.2 }}>{name}</div>
            {contact.contact_accounts__title ? (
              <div style={{ color: 'rgba(0,0,0,0.45)', fontSize: 13 }}>{text(contact.contact_accounts__title)}</div>
            ) : null}
            {contact.prog__account_name ? (
              contact.contact_accounts__account_id ? (
                <Typography.Link
                  onClick={() => navigate(detailPath('companies', contact.contact_accounts__account_id))}
                  style={{ fontSize: 13 }}
                >
                  {text(contact.prog__account_name)}
                </Typography.Link>
              ) : (
                <div style={{ color: 'rgba(0,0,0,0.45)', fontSize: 13 }}>{text(contact.prog__account_name)}</div>
              )
            ) : null}
          </div>
        </div>
      </Card>

      <Section title="Contact">
        {methods.length === 0 ? (
          <Typography.Text type="secondary">No contact details.</Typography.Text>
        ) : (
          <List
            size="small"
            dataSource={methods}
            rowKey={(m) => m.key}
            renderItem={(m) => (
              <List.Item style={{ paddingLeft: 4, paddingRight: 4 }}>
                <a href={m.href} target={m.key === 'linkedin' ? '_blank' : undefined} rel="noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', color: 'inherit' }}>
                  <FontAwesomeIcon icon={m.icon} style={{ color: '#1890ff', width: 18 }} />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.45)' }}>{m.label}</div>
                    <div style={{ wordBreak: 'break-all' }}>{m.value}</div>
                  </div>
                </a>
              </List.Item>
            )}
          />
        )}
      </Section>

      <Section title="Roles">
        <CompanyRolesList rows={roles} emptyText="No current roles." navigate={navigate} />
      </Section>

      {priorRoles.length > 0 ? (
        <Section title="Prior roles">
          <CompanyRolesList rows={priorRoles} emptyText="" navigate={navigate} />
        </Section>
      ) : null}

      {founderOf.length > 0 ? (
        <Section title="Founder of">
          <CompanyRolesList rows={founderOf} emptyText="" navigate={navigate} />
        </Section>
      ) : null}

      {introductions.length > 0 ? (
        <Section title="Introductions">
          <CompanyRolesList rows={introductions} emptyText="" navigate={navigate} />
        </Section>
      ) : null}
    </div>
  )
}

export default ContactScreen
